export type MessageRole = 'user' | 'assistant' | 'system';
export type MessageStatus = 'sending' | 'streaming' | 'complete' | 'error';

export type ContentBlock =
  | { type: 'text'; text: string }
  | { type: 'markdown'; markdown: string }
  | {
      type: 'action_chips';
      chips: { label: string; prompt: string }[];
    }
  | {
      type: 'workspace_trigger';
      route: string;
      label: string;
      params?: Record<string, string>;
    }
  | { type: 'error'; message: string; retryable: boolean }
  | {
      type: 'attention_card';
      title: string;
      bullets: string[];
      cta?: { label: string; route: string; params?: Record<string, string> };
    };

export interface ChatMessage {
  id: string;
  conversationId: string;
  role: MessageRole;
  content: ContentBlock[];
  status: MessageStatus;
  createdAt: string; // ISO 8601
}

export interface Conversation {
  id: string;
  messages: ChatMessage[];
  createdAt: string;
}

/** SSE event types from the streaming API. */
export type SSEEventType =
  | 'message_start'
  | 'content_block_start'
  | 'content_block_delta'
  | 'content_block_stop'
  | 'message_complete'
  | 'error'
  | 'ping';

export interface SSEEvent {
  event: SSEEventType;
  data: Record<string, unknown>;
}

export interface MessageStartData {
  messageId: string;
  conversationId: string;
}

export interface ContentBlockStartData {
  messageId: string;
  blockIndex: number;
  blockType: ContentBlock['type'];
}

export interface ContentBlockDeltaData {
  messageId: string;
  blockIndex: number;
  delta: string;
}

export interface ContentBlockStopData {
  messageId: string;
  blockIndex: number;
}

export interface MessageCompleteData {
  messageId: string;
  /**
   * Authoritative final message. The live ai-mcp-server sends `content` as a
   * plain string (not the documented `ContentBlock[]`), and omits
   * `conversationId` / `status` — so this is the raw wire shape, not a clean
   * `ChatMessage`. Always pass it through `normalizeMessageContent` before
   * merging into the store.
   */
  message: Omit<ChatMessage, 'content'> & { content: ContentBlock[] | string };
}

/**
 * Coerces a wire `message.content` into `ContentBlock[]`. The live
 * ai-mcp-server sends a bare string on `message_complete`; the MSW mock + the
 * documented contract send `ContentBlock[]`. Returns `undefined` when content
 * is absent so callers can keep already-accumulated streaming blocks instead
 * of clobbering them with an empty array.
 */
export function normalizeMessageContent(
  content: ContentBlock[] | string | undefined | null,
): ContentBlock[] | undefined {
  if (typeof content === 'string') return [{ type: 'text', text: content }];
  if (Array.isArray(content)) return content;
  return undefined;
}

export interface SSEErrorData {
  code: string;
  message: string;
  retryable: boolean;
}
